import { create } from 'zustand';
import type { Order, CreateOrderData, FilterConfig, OrderStatus, OrderDetails, UpdateShippingData } from '../types';
import { orderService } from '../services/order.service';

interface OrderState {
  orders: Order[];
  selectedOrder: OrderDetails | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: FilterConfig;

  // Actions
  fetchOrders: (page?: number, filters?: FilterConfig) => Promise<void>;
  getOrderById: (id: string) => Promise<Order>;
  createOrder: (data: CreateOrderData) => Promise<Order>;
  updateOrderStatus: (id: string, status: OrderStatus) => Promise<void>;
  deleteOrder: (id: string) => Promise<void>;
  cancelOrder: (id: string) => Promise<void>;
  refundTransaction: (orderId: string, transactionId: string) => Promise<void>;
  updateShipping: (orderId: string, data: UpdateShippingData) => Promise<OrderDetails>;
  setSelectedOrder: (order: Order | null) => void;
  setFilters: (filters: Partial<FilterConfig>) => void;
  setPage: (page: number) => void;
  clearError: () => void;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  orders: [],
  selectedOrder: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  filters: { search: '' },

  fetchOrders: async (page?: number, filters?: FilterConfig) => {
    const { currentPage, pageSize, filters: currentFilters } = get();
    const pageToFetch = page ?? currentPage;
    const filtersToUse = filters ?? currentFilters;

    set({ isLoading: true, error: null });
    try {
      const response = await orderService.getOrders(pageToFetch, pageSize, filtersToUse);
      set({
        orders: response.data,
        totalCount: response.total,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch orders';
      set({ error: message, isLoading: false });
    }
  },

  getOrderById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const order = await orderService.getOrderById(id) as OrderDetails;
      set({ selectedOrder: order, isLoading: false });
      return order;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch order';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  createOrder: async (data: CreateOrderData) => {
    set({ isLoading: true, error: null });
    try {
      const newOrder = await orderService.createOrder(data);
      set((state) => ({
        orders: [newOrder, ...state.orders],
        isLoading: false,
      }));
      return newOrder;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create order';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateOrderStatus: async (id: string, status: OrderStatus) => {
    set({ isLoading: true, error: null });
    try {
      const updatedOrder = await orderService.updateOrderStatus(id, status);
      set((state) => ({
        orders: state.orders.map((o) => (o.id === id ? updatedOrder : o)),
        selectedOrder: state.selectedOrder?.id === id ? updatedOrder : state.selectedOrder,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update order';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteOrder: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await orderService.deleteOrder(id);
      set((state) => ({
        orders: state.orders.filter((o) => o.id !== id),
        selectedOrder: state.selectedOrder?.id === id ? null : state.selectedOrder,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete order';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  cancelOrder: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await orderService.cancelOrder(id);
      set((state) => ({
        orders: state.orders.filter((o) => o.id !== id),
        selectedOrder: null,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel order';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  refundTransaction: async (orderId: string, transactionId: string) => {
    set({ isLoading: true, error: null });
    try {
      await orderService.refundTransaction(orderId, transactionId);
      // Refresh the order to get updated transaction status
      const order = await orderService.getOrderById(orderId) as OrderDetails;
      set({ selectedOrder: order, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to refund transaction';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateShipping: async (orderId: string, data: UpdateShippingData) => {
    set({ isLoading: true, error: null });
    try {
      const order = await orderService.updateShipping(orderId, data) as OrderDetails;
      set({ selectedOrder: order, isLoading: false });
      return order;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update shipping';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setSelectedOrder: (order: Order | null) => set({ selectedOrder: order }),

  setFilters: (filters: Partial<FilterConfig>) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      currentPage: 1,
    }));
  },

  setPage: (page: number) => set({ currentPage: page }),

  clearError: () => set({ error: null }),
}));



